import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Spinner, Alert, Modal, Form } from "react-bootstrap";
import moment from "moment";
import BlogView from "../../pages/blogview/BlogView";
import Buttons from "../../components/buttons/Buttons";
import { AxiosInstance } from "../../axios";
import ToastMessage from "../../components/messagetoast/ToastMessage";
import Cookies from "js-cookie";
import styles from "../job_view/JobView.module.css";

function AdminBlogView() {
  const { id: blogId } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedBlogStatus, setSelectedBlogStatus] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  useEffect(() => {
    if (!blogId) {
      setError("No blog ID provided. Redirecting...");
      setTimeout(() => navigate("/admin/blog-list"), 2000);
      return;
    }
    fetchBlogDetails(blogId);
  }, [blogId, navigate]);

  const fetchBlogDetails = async (id) => {
    try {
      const response = await AxiosInstance.post("/api/blog/view", { id });
      if (response.data.status) {
        const blogData = response.data.blog;
        setBlog(blogData);
        setSelectedBlogStatus(
          blogData.blog_status === 1 ? "Active" : "Inactive",
        );
      } else {
        setError("Failed to fetch blog.");
      }
    } catch (err) {
      console.error(err);
      setError("API Error: Could not fetch blog data.");
    } finally {
      setLoading(false);
    }
  };

  const handleShowStatusModal = () => setShowStatusModal(true);
  const handleCloseStatusModal = () => setShowStatusModal(false);

  const handleBlogStatusRadioChange = (e) => {
    setSelectedBlogStatus(e.target.value);
  };

  const handleSaveBlogStatus = async () => {
    try {
      setLoading(true);
      const newStatus = selectedBlogStatus === "Active" ? 1 : 0;

      const userId = Cookies.get("user_id");
      const userType = Cookies.get("user_type");

      if (!userId || !userType) {
        setToast({
          show: true,
          message:
            "Authentication error: User ID or Type not found in cookies.",
          variant: "danger",
        });
        setLoading(false);
        return;
      }

      let formattedCategories = {};

      if (blog.categories_id && blog.categorie_name) {
        formattedCategories = {
          categories_id: blog.categories_id,
          categories_name: blog.categorie_name,
        };
      }

      // Format tags
      const formattedTags = blog.tag
        ? blog.tag.map((t) => ({
            tag_id: t.tag_id,
            tag_name: t.tag_name,
          }))
        : [];

      const payload = {
        id: blogId,
        title: blog.title,
        tagline: blog.tagline,
        meta_title: blog.meta_title || "",
        meta_description: blog.meta_description || "",
        content: blog.content,
        youtube_url: blog.youtube_url || "",
        author_name: blog.author_name,
        author_designation: blog.author_designation,
        categories_id: formattedCategories.categories_id,
        categories_name: formattedCategories.categories_name,
        tag: formattedTags,
        cover_image_originalname: blog.cover_image_originalname,
        cover_image_path: blog.cover_image_path,
        blog_status: newStatus,
        deleted: blog.deleted || 0,
        user_type: parseInt(userType, 10),
        updated_by: userId,
      };

      const response = await AxiosInstance.post("/api/blog/update", payload);

      if (response.data.status) {
        setBlog((prevBlog) => ({ ...prevBlog, blog_status: newStatus }));
        setToast({
          show: true,
          message: "Blog status updated successfully!",
          variant: "success",
        });
      } else {
        setToast({
          show: true,
          message: `Failed to update status: ${response.data.message}`,
          variant: "danger",
        });
      }
    } catch (err) {
      console.error("Status update error:", err);
      setToast({
        show: true,
        message: "API Error: Could not update blog status.",
        variant: "danger",
      });
    } finally {
      setLoading(false);
      handleCloseStatusModal();
    }
  };

  const handleEditBlog = () => {
    const userId = Cookies.get("user_id");
    const userType = Cookies.get("user_type");

    if (!userId || !userType) {
      setToast({
        show: true,
        message: "Authentication error: User ID or Type not found for editing.",
        variant: "danger",
      });
      return;
    }

    // Format categories for edit with categories_name field
    const formattedCategoriesForEdit =
      blog.categories_id && blog.categorie_name
        ? [
            {
              categories_id: blog.categories_id,
              categories_name: blog.categorie_name, // Using categories_name as required
            },
          ]
        : [];

    const formattedTagsForEdit = blog.tag
      ? blog.tag.map((t) => ({
          tag_id: t.tag_id,
          tag_name: t.tag_name,
        }))
      : [];

    const blogDataForEdit = {
      ...blog,
      categories: formattedCategoriesForEdit,
      tag: formattedTagsForEdit,
      user_type: parseInt(userType, 10),
      updated_by: userId,
      id: blogId,
      deleted: blog.deleted || 0,
    };

    navigate(`/admin/create-blog`, {
      state: { blogId: blog.id, blogData: blogDataForEdit, editBlog: true },
    });
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        {/* <Spinner animation="border" />
        <p>Loading blog details...</p> */}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  if (!blog) {
    return (
      <Alert variant="info" className="mt-3">
        Blog data not found.
      </Alert>
    );
  }

  const renderField = (label, value, button = null) => (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between">
        <div
          style={{
            fontWeight: 600,
            color: "var(--primary-color)",
            opacity: 0.6,
            fontSize: "18px",
          }}
        >
          {label}
        </div>
        {button}
      </div>
      <div
        style={{
          fontWeight: 600,
          color: "var(--primary-color)",
          fontSize: "20px",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div>
      <Card className="p-3 shadow-sm rounded-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <h5
            className="mb-3 mb-md-0"
            style={{
              borderLeft: "4px solid #FA8316",
              paddingLeft: "10px",
              lineHeight: "1.5",
            }}
          >
            Blog Details
          </h5>

          <div className={styles.buttonRow}>
            <Buttons
              variant="secondary"
              className="me-2"
              text="Change Status"
              onClick={handleShowStatusModal}
            />
            <Buttons
              variant="secondary"
              icon={<i className="bi bi-pencil-square me-1" />}
              text="Edit Blog"
              onClick={handleEditBlog}
            />
          </div>
        </div>

        <Row>
          <Col md={3}>{renderField("Posted by", blog.posted_by_name)}</Col>
          <Col md={3}>
            {renderField(
              "Posted Date",
              moment(blog.posted_date).format("DD/MM/YYYY"),
            )}
          </Col>
          <Col md={3}>
            {renderField(
              "Status",
              blog.blog_status === 1 ? "Active" : "Inactive",
            )}
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            {renderField("Category", blog.categorie_name || "N/A")}
          </Col>
          <Col md={9}>
            {renderField(
              "Tags",
              blog.tag?.map((t) => t.tag_name).join(", ") || "N/A",
            )}
          </Col>
        </Row>
      </Card>

      <div className="mt-4">
        <BlogView blogId={blogId} showHeaderIcons={false} />
      </div>

      <Modal show={showStatusModal} onHide={handleCloseStatusModal}>
        <Modal.Header closeButton>
          <Modal.Title>Change Blog Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {["Active", "Inactive"].map((status) => (
              <Form.Check
                key={status}
                type="radio"
                label={status}
                name="blogStatus"
                id={`blogStatus-${status}`}
                value={status}
                checked={selectedBlogStatus === status}
                onChange={handleBlogStatusRadioChange}
                className="mb-2 custom-radio"
              />
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Buttons
            type="button"
            variant="outline-secondary"
            onClick={handleCloseStatusModal}
            text="Cancel"
          />
          <Buttons
            type="button"
            variant="primary"
            onClick={handleSaveBlogStatus}
            text="Save"
          />
        </Modal.Footer>
      </Modal>

      <ToastMessage
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        message={toast.message}
        variant={toast.variant}
      />
    </div>
  );
}

export default AdminBlogView;
